import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { DependencyManager } from '../components/DependencyManager';
import { ContextUpdate } from '../components/ContextUpdate';

const GET_CONTEXT = gql`
  query GetContext($id: ID!) {
    context(id: $id) {
      id
      name
      version
      type
      content
      tags
      created_at
      created_by
    }
  }
`;

const GET_VERSION_HISTORY = gql`
  query GetVersionHistory($name: String!) {
    versionHistory(name: $name) {
      id
      version
      content
      created_at
    }
  }
`;

export function ContextDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [showUpdate, setShowUpdate] = useState(false);

  const { data, loading, error } = useQuery(GET_CONTEXT, {
    variables: { id },
  });

  const context = data?.context;

  const { data: historyData } = useQuery(GET_VERSION_HISTORY, {
    variables: { name: context?.name },
    skip: !context,
  });

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-500">Error: {error.message}</div>;
  if (!context) return <div className="p-4">Context not found</div>;

  const versions = historyData?.versionHistory || [];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-4">
        <Link to="/" className="text-primary hover:underline">
          ← Back to Contexts
        </Link>
      </div>

      <div className="bg-white border rounded-lg p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold">{context.name}</h1>
            <div className="flex gap-3 mt-2 text-sm text-gray-600">
              <span>v{context.version}</span>
              <span>•</span>
              <span className="capitalize">{context.type}</span>
              <span>•</span>
              <span>{new Date(context.created_at).toLocaleDateString()}</span>
            </div>
          </div>
          <button
            onClick={() => setShowUpdate(!showUpdate)}
            className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90"
          >
            {showUpdate ? 'Cancel' : 'Update Context'}
          </button>
        </div>

        {context.tags.length > 0 && (
          <div className="flex gap-2 mb-4">
            {context.tags.map((tag: string) => (
              <span key={tag} className="text-xs bg-gray-100 px-2 py-1 rounded">
                {tag}
              </span>
            ))}
          </div>
        )}

        {showUpdate ? (
          <ContextUpdate
            name={context.name}
            currentVersion={context.version}
            currentContent={context.content}
            onClose={() => setShowUpdate(false)}
          />
        ) : (
          <div className="prose max-w-none">
            <h3 className="text-lg font-semibold mb-2">Content</h3>
            <pre className="whitespace-pre-wrap bg-gray-50 p-4 rounded border text-sm">
              {context.content}
            </pre>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <DependencyManager contextName={context.name} />
        </div>

        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-bold mb-4">Version History</h3>
          {versions.length === 0 ? (
            <p className="text-sm text-gray-500">No version history</p>
          ) : (
            <div className="space-y-2">
              {versions.map((v: any) => (
                <div
                  key={v.id}
                  className={`p-3 rounded border ${
                    v.id === context.id
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium">v{v.version}</span>
                    <span className="text-xs text-gray-500">
                      {new Date(v.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {v.id === context.id && (
                    <span className="text-xs text-primary font-medium">Current</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
